import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';

import { Watchlist } from '../../types/Watchlist';
import { WatchlistHeader } from './WatchlistHeader';

// O8 — the design shows 25 items over pages of 10; the frozen shape has no server pagination, so
// this is a client-side assumption over the array already in hand. Change here if it turns out wrong.
const PAGE_SIZE = 10;

interface WatchlistListProps {
  watchlist: Watchlist;
}

export const WatchlistList = ({ watchlist }: WatchlistListProps) => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(0);

  const { items, generatedAt } = watchlist;
  const pageCount = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Box>
      <WatchlistHeader generatedAt={generatedAt} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '28px', mb: 2 }}>
        <Typography variant="h3">{t('application.watchlist.itemsFound', { count: items.length })}</Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <IconButton
            aria-label={t('application.watchlist.pagination.previous') ?? undefined}
            disabled={page === 0}
            onClick={() => setPage(current => current - 1)}
            sx={{ '&.Mui-disabled': { bgcolor: 'transparent' } }}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <IconButton
            aria-label={t('application.watchlist.pagination.next') ?? undefined}
            disabled={page >= pageCount - 1}
            onClick={() => setPage(current => current + 1)}
            sx={{ '&.Mui-disabled': { bgcolor: 'transparent' } }}
          >
            <ArrowForwardIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
      </Box>

      <Box>
        {pageItems.map(item => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 3,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'grey.500',
              '&:not(:first-of-type)': { borderTop: 'none' },
              '&:first-of-type': { borderRadius: '20px 20px 0 0' },
              '&:last-of-type': { borderRadius: '0 0 20px 20px' },
              // A lone row matches both rules above; equal specificity, same shorthand, so
              // `:last-of-type` would win and square the top. Must stay last.
              '&:only-of-type': { borderRadius: '20px' },
            }}
          >
            <Typography variant="h4">{item.title}</Typography>
            <Typography variant="body1">{item.justification}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
